import { ArrowUpRight, Building2 } from "lucide-react";
import {
  ListBase,
  RecordContextProvider,
  useListContext,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Client } from "../types";

export const ClientList = () => (
  <ListBase perPage={100} sort={{ field: "name", order: "ASC" }}>
    <ClientListContent />
  </ListBase>
);

const ClientListContent = () => {
  const translate = useTranslate();
  const { data = [], isPending, total } = useListContext<Client>();
  if (isPending) return null;

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex items-baseline gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">
          {translate("resources.clients.name", { smart_count: 2 })}
        </h1>
        <span className="text-muted-foreground text-lg">{total ?? 0}</span>
      </div>

      {data.length === 0 ? (
        <EmptyClients />
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
          {data.map((client) => (
            <RecordContextProvider key={client.id} value={client}>
              <ClientCard client={client} />
            </RecordContextProvider>
          ))}
        </div>
      )}
    </div>
  );
};

const ClientCard = ({ client }: { client: Client }) => {
  const translate = useTranslate();
  const { dealCategories } = useConfigurationContext();
  const serviceLabels = client.services
    .map((value) => dealCategories.find((c) => c.value === value)?.label)
    .filter(Boolean);

  return (
    <Link
      to={`/clients/${client.id}/show`}
      className="group relative rounded-[22px] bg-card p-5 no-underline text-card-foreground transition-colors hover:bg-accent"
    >
      <span className="absolute -top-1.5 -right-1.5 grid size-11 place-items-center rounded-full border-[6px] border-background bg-card text-muted-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        <ArrowUpRight className="size-4" />
      </span>
      <div className="flex items-center gap-3 pr-8">
        <CompanyAvatar />
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold">{client.name}</div>
          <div className="text-sm text-muted-foreground">
            {client.service_mode
              ? translate(
                  `resources.clients.service_modes.${client.service_mode}`,
                )
              : translate("resources.clients.not_set")}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {serviceLabels.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            {translate("resources.clients.not_set")}
          </span>
        ) : (
          serviceLabels.map((label) => (
            <span
              key={label}
              className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground"
            >
              {label}
            </span>
          ))
        )}
      </div>
    </Link>
  );
};

const EmptyClients = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col items-center gap-3 rounded-[22px] bg-card px-6 py-16 text-center">
      <Building2 className="size-8 text-muted-foreground" />
      <h2 className="text-xl font-semibold">
        {translate("resources.clients.empty.title")}
      </h2>
      <p className="max-w-md text-muted-foreground">
        {translate("resources.clients.empty.description")}
      </p>
    </div>
  );
};
